import { useEffect, useState, useCallback, useRef } from 'react';
import { getWebSocketService, isWebSocketServiceInitialized, WebSocketMessage } from '../services/websocketService';

interface UseWebSocketOptions {
  autoConnect?: boolean;
  messageTypes?: string[];
}

interface UseWebSocketReturn {
  isConnected: boolean;
  sendMessage: (message: WebSocketMessage) => void;
  lastMessage: WebSocketMessage | null;
  connect: () => void;
  disconnect: () => void;
}

/**
 * React hook for WebSocket communication
 * Uses the lazy-initialized WebSocket service singleton
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { autoConnect = true, messageTypes = ['*'] } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const unsubscribesRef = useRef<(() => void)[]>([]);

  useEffect(() => {
    // Only proceed if WebSocket service has been initialized (by ConfigProvider)
    if (!isWebSocketServiceInitialized()) {
      console.warn('WebSocket service not yet initialized. Waiting for config...');
      return;
    }

    const wsService = getWebSocketService();

    // Subscribe to connection changes
    const unsubConnect = wsService.onConnectionChange(setIsConnected);
    unsubscribesRef.current.push(unsubConnect);

    // Subscribe to specified message types
    messageTypes.forEach(messageType => {
      const unsubMessage = wsService.on(messageType, (data) => {
        setLastMessage(data);
      });
      unsubscribesRef.current.push(unsubMessage);
    });

    // Auto-connect if enabled
    if (autoConnect) {
      wsService.connect();
    }

    // Cleanup on unmount
    return () => {
      unsubscribesRef.current.forEach(unsub => unsub());
      unsubscribesRef.current = [];
    };
  }, []); // Only run on mount

  const sendMessage = useCallback((message: WebSocketMessage) => {
    if (isWebSocketServiceInitialized()) {
      getWebSocketService().send(message);
    } else {
      console.warn('Cannot send message: WebSocket service not initialized');
    }
  }, []);

  const connect = useCallback(() => {
    if (isWebSocketServiceInitialized()) {
      getWebSocketService().connect();
    }
  }, []);

  const disconnect = useCallback(() => {
    if (isWebSocketServiceInitialized()) {
      getWebSocketService().disconnect();
    }
  }, []);

  return {
    isConnected,
    sendMessage,
    lastMessage,
    connect,
    disconnect
  };
}

/**
 * Hook for subscribing to specific WebSocket message types
 */
export function useWebSocketMessage<T = any>(
  messageType: string,
  handler: (data: T) => void
): void {
  useEffect(() => {
    if (!isWebSocketServiceInitialized()) {
      return;
    }
    const unsubscribe = getWebSocketService().on(messageType, handler);
    return unsubscribe;
  }, [messageType, handler]);
}

/**
 * Hook for detection-specific WebSocket communication
 */
export function useDetectionWebSocket(model: string = 'dev') {
  const [detectionStatus, setDetectionStatus] = useState<string>('idle');
  const [detectionResult, setDetectionResult] = useState<any>(null);
  
  const { isConnected, sendMessage } = useWebSocket({
    messageTypes: ['detection_status', 'detection_complete']
  });

  useWebSocketMessage('detection_status', (data) => {
    setDetectionStatus(data.status);
  });

  useWebSocketMessage('detection_complete', (data) => {
    setDetectionStatus('complete');
    setDetectionResult(data);
  });

  const startDetection = useCallback(() => {
    console.log('📸 startDetection called - sending WebSocket message with model:', model);
    sendMessage({ type: 'start_detection', model });
    setDetectionStatus('detecting');
  }, [sendMessage, model]);

  return {
    isConnected,
    detectionStatus,
    detectionResult,
    startDetection
  };
}
